import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { TargetingSearchResult } from '../../../types/metaAdResources';
import { useDebouncedValue } from '../useDebouncedValue';

// Shared remote-search + chip-list control for the targeting typeaheads
// (countries / regions / cities / interests; behaviors/languages/zips reuse it in
// Phase 2). `search` is bound to the account + country by the caller; results are
// debounced (300ms) and filtered against the current selection. Chips render via
// a render-prop so cities can add radius controls.

export type ChipItem = { id: string; name: string };

export function TargetingSearchField<Item extends ChipItem>({
  label,
  placeholder,
  searchKey,
  search,
  selected,
  onAdd,
  onRemove,
  renderChip,
}: {
  label: string;
  placeholder: string;
  searchKey: string; // disambiguates the query cache (e.g. 'interests')
  search: (q: string) => Promise<TargetingSearchResult[]>;
  selected: Item[];
  onAdd: (result: TargetingSearchResult) => void;
  onRemove: (id: string) => void;
  renderChip?: (item: Item, remove: () => void) => ReactNode;
}) {
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q.trim(), 300);

  const results = useQuery({
    queryKey: ['metaAdTargetingSearch', searchKey, debounced],
    queryFn: () => search(debounced),
    enabled: debounced.length > 0,
  });

  const selectedIds = new Set(selected.map((s) => s.id));
  const visible = (results.data ?? []).filter((r) => !selectedIds.has(r.id)).slice(0, 8);

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
        {debounced.length > 0 && visible.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-md">
            {visible.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => {
                    onAdd(r);
                    setQ('');
                  }}
                  className="flex w-full flex-col px-3 py-1.5 text-left text-sm hover:bg-slate-50"
                >
                  <span>{r.name}</span>
                  {r.path && r.path.length > 0 && (
                    <span className="text-xs text-slate-400">{r.path.join(' › ')}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selected.map((item) =>
            renderChip ? (
              <span key={item.id}>{renderChip(item, () => onRemove(item.id))}</span>
            ) : (
              <span
                key={item.id}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700"
              >
                {item.name}
                <button type="button" aria-label={`Remove ${item.name}`} onClick={() => onRemove(item.id)}>
                  <X className="h-3 w-3 text-slate-400 hover:text-slate-600" />
                </button>
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}
